import { FC, useMemo } from 'react';

import { PropertyOptionIcon } from './PropertyOptionIcon';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../../../components/ui/Tooltip';
import { PropertyOption } from '../../../types/shared';
import { cn } from '../../../utils';

interface Props {
    readonly value?: string;
    readonly options: PropertyOption[];
    readonly onChange: (value: string) => void;
    readonly testId: string;
}

const ALL_VALUE = 'all';

export const IconPropertyFilter: FC<Props> = ({
    value = ALL_VALUE,
    options,
    onChange,
    testId,
}) => {
    const selectedValues = useMemo(
        () =>
            new Set(
                value === ALL_VALUE
                    ? []
                    : value
                          .split(',')
                          .map((item) => item.trim())
                          .filter(Boolean),
            ),
        [value],
    );

    const toggleOption = (slug: string) => {
        const nextValues = new Set(selectedValues);

        if (nextValues.has(slug)) {
            nextValues.delete(slug);
        } else {
            nextValues.add(slug);
        }

        const orderedValues = options
            .map((option) => option.slug)
            .filter((optionSlug) => nextValues.has(optionSlug));

        onChange(
            orderedValues.length > 0 ? orderedValues.join(',') : ALL_VALUE,
        );
    };

    return (
        <TooltipProvider delayDuration={150} skipDelayDuration={100}>
            <div className="flex flex-wrap gap-1.5" data-testid={testId}>
                {options.map((option) => {
                    const selected = selectedValues.has(option.slug);

                    return (
                        <Tooltip key={option.id}>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    aria-label={option.name}
                                    aria-pressed={selected}
                                    data-testid={`${testId}-option-${option.slug}`}
                                    onClick={() => toggleOption(option.slug)}
                                    className={cn(
                                        'inline-flex items-center justify-center rounded-full p-0.5 align-middle leading-none ring-offset-background transition hover:scale-105 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none',
                                        selected &&
                                            'bg-primary/15 ring-2 ring-primary',
                                    )}
                                >
                                    <PropertyOptionIcon
                                        iconKey={option.icon_key}
                                        label={option.name}
                                        className="h-7 w-7"
                                        fallback
                                    />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="px-2 py-1">
                                {option.name}
                            </TooltipContent>
                        </Tooltip>
                    );
                })}
            </div>
        </TooltipProvider>
    );
};
